import React, { useEffect, useRef, useState } from 'react';
import {
    createContent,
    uploadCover,
    getContentById,
    updateContent,
    assignTagsToContent,
    addAudios,
    listAudios,
    updateAudio,
    deleteAudio,
    getTranslations,
    upsertTranslation,
    patchTranslation,
    deleteTranslation,
    deleteCover,
    type AudioMeta,
} from '../../../../api/contents';
import { searchAuthors } from '../../../../api/authors';
import { searchTags } from '../../../../api/tags';
import { listCategories } from '../../../../api/categories';
import AddQuiz from '../Quiz/AddQuiz';

import '../../../../assets/styles/add-contents.scss';
import { useParams, useNavigate } from 'react-router-dom';

/* ================== Stałe / Typy ================== */

const AGE_CATEGORIES = ['3+', '7+', '12+', '16+', '18+'] as const;

type ApiLang = AudioMeta['lang'];            // 'PL' | 'EN' | 'ES' | 'DE' | 'UK'
type Lang = ApiLang | 'IT' | 'FR';           // rozszerzamy o IT, FR (frontendowo)

type StorageType = 'UPLOAD' | 'LINK';
type Difficulty = 'EASY' | 'MEDIUM' | 'HARD';

type AudioRow = {
    id: number;
    url: string;              // file_path
    lang: Lang;
    narrator: string | null;
    duration: number | null;
    license?: string | null;
    storage_type?: StorageType;
    source_url?: string | null;
    is_visible?: boolean;
};

type TranslationRow = {
    id: number;
    content_id: number;
    language_code: Lang;
    translated_text: string | null;
    translated_description: string | null;
    created_by?: number | null;
    last_edited_by?: number | null;
    last_edited_at?: string | null;
    is_visible?: boolean;
    audios: AudioRow[];
};

const ALLOWED: Lang[] = ['PL', 'EN', 'ES', 'DE', 'UK', 'IT', 'FR'];
// języki dostępne dla tłumaczeń (bez PL)
const TRANSLATION_LANGS: Lang[] = ALLOWED.filter(l => l !== 'PL');

function ensureLang(v: string | undefined | null): Lang {
    const U = String(v || '').toUpperCase();
    return (ALLOWED as string[]).includes(U) ? (U as Lang) : 'PL';
}

interface Props {
    contentId?: number | null;
    onDone?: () => void;
    onCancel?: () => void;
}

interface FormData {
    title: string;
    description: string;
    text_content: string;
    read_time_minutes: string | number;
    age_category: string;
    is_visible: boolean;
    license: string;
    source_url: string;
    language: string;
    type: 'TEXT' | 'AUDIO' | 'BOTH';
    is_premium: boolean;
    category_id: number | null;
    author_ids: number[];
    tag_ids: number[];
    difficulty_level?: Difficulty | null;
}

/* ================== Utils ================== */

const getAudioDuration = (file: File): Promise<number | null> =>
    new Promise((resolve) => {
        const audio = new Audio();
        audio.preload = 'metadata';
        audio.onloadedmetadata = () => {
            URL.revokeObjectURL(audio.src);
            resolve(Number.isFinite(audio.duration) ? Math.round(audio.duration) : null);
        };
        audio.onerror = () => resolve(null);
        audio.src = URL.createObjectURL(file);
    });

/* ================== UI: Dialogi ================== */

function Dialog({
    open,
    title,
    message,
    onClose,
    variant = 'ok',
}: {
    open: boolean;
    title?: string;
    message?: string;
    variant?: 'ok' | 'error';
    onClose: () => void;
}) {
    if (!open) return null;
    return (
        <div className="modal-backdrop">
            <div className={`modal-card ${variant === 'error' ? 'modal-error' : ''}`}>
                {title && <h3>{title}</h3>}
                {message && <p style={{ whiteSpace: 'pre-wrap' }}>{message}</p>}
                <div className="modal-actions">
                    <button className="btn-primary" onClick={onClose}>OK</button>
                </div>
            </div>
        </div>
    );
}

/** Uniwersalny dialog potwierdzający – zastępuje window.confirm */
function ConfirmDialog({
    open,
    title = 'Potwierdź',
    message,
    confirmLabel = 'Potwierdź',
    cancelLabel = 'Anuluj',
    danger = false,
    onConfirm,
    onCancel,
}: {
    open: boolean;
    title?: string;
    message?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    danger?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}) {
    if (!open) return null;
    return (
        <div className="modal-backdrop">
            <div className="modal-card">
                {title && <h3>{title}</h3>}
                {message && <p style={{ whiteSpace: 'pre-wrap' }}>{message}</p>}
                <div className="modal-actions">
                    <button className="btn-ghost" onClick={onCancel}>{cancelLabel}</button>
                    <button className={danger ? 'delete-btn' : 'btn-primary'} onClick={onConfirm}>{confirmLabel}</button>
                </div>
            </div>
        </div>
    );
}

/** Ładny picker plików: ukryty input + przycisk */
function FilePicker({
    accept,
    multiple,
    onChange,
    disabled,
    buttonLabel,
    inputRef,
    className,
}: {
    accept?: string;
    multiple?: boolean;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    disabled?: boolean;
    buttonLabel?: string;
    inputRef?: React.RefObject<HTMLInputElement | null>;
    className?: string;
}) {
    const localRef = useRef<HTMLInputElement | null>(null);
    const ref = inputRef || localRef;

    return (
        <div className={`file-picker ${className || ''}`}>
            <input
                type="file"
                accept={accept}
                multiple={multiple}
                ref={ref}
                onChange={onChange}
                disabled={disabled}
                className="file-input-hidden"
            />
            <button
                type="button"
                className="btn-file"
                onClick={() => ref.current?.click()}
                disabled={disabled}
            >
                {buttonLabel || 'Wybierz plik'}
            </button>
        </div>
    );
}

/* ================== Modale: Audio ================== */

function LinkAudiosModal({
    open,
    defaultLang,
    lockLang = false,
    onClose,
    onSubmit,
}: {
    open: boolean;
    defaultLang: Lang;
    lockLang?: boolean;
    onClose: () => void;
    onSubmit: (metas: AudioMeta[]) => void;
}) {
    const [rows, setRows] = useState<Array<{
        lang: Lang;
        url: string;
        narrator: string;
        license: string;
        visible: boolean;
    }>>([]);

    useEffect(() => {
        if (!open) return;
        setRows([{ lang: defaultLang, url: '', narrator: '', license: '', visible: false }]);
    }, [open, defaultLang]);

    if (!open) return null;

    const update = (i: number, patch: Partial<(typeof rows)[number]>) =>
        setRows(prev => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

    const addRow = () =>
        setRows(prev => [...prev, { lang: defaultLang, url: '', narrator: '', license: '', visible: false }]);

    const removeRow = (i: number) =>
        setRows(prev => (prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev));

    const isValidUrl = (s: string) => /^https?:\/\//i.test(s.trim());
    const allValid = rows.length > 0 && rows.every(r => isValidUrl(r.url) && r.narrator.trim().length > 0);

    return (
        <div className="modal-backdrop">
            <div className="modal-card">
                <h3>Dodaj wiele linków do audio</h3>
                <div className="modal-table">
                    {rows.map((r, i) => (
                        <div key={i} className="modal-row">
                            <div className="cell">
                                <label>Język</label>
                                <select
                                    value={r.lang}
                                    onChange={e => {
                                        if (lockLang) return;
                                        update(i, { lang: ensureLang(e.target.value) });
                                    }}
                                    disabled={lockLang}
                                >
                                    {ALLOWED.map(l => <option key={l} value={l}>{l}</option>)}
                                </select>
                            </div>
                            <div className="cell" style={{ flex: 2 }}>
                                <label>URL</label>
                                <input
                                    value={r.url}
                                    onChange={e => update(i, { url: e.target.value })}
                                    placeholder="https://..."
                                />
                            </div>
                            <div className="cell">
                                <label>Lektor *</label>
                                <input
                                    value={r.narrator}
                                    onChange={e => update(i, { narrator: e.target.value })}
                                    placeholder="np. Jan Kowalski"
                                />
                            </div>
                            <div className="cell">
                                <label>Licencja</label>
                                <input
                                    value={r.license}
                                    onChange={e => update(i, { license: e.target.value })}
                                    placeholder="np. CC-BY"
                                />
                            </div>
                            <div className="cell">
                                <label>Widoczne</label>
                                <input
                                    type="checkbox"
                                    checked={r.visible}
                                    onChange={e => update(i, { visible: e.target.checked })}
                                />
                            </div>
                            <div className="cell">
                                <button className="btn-ghost" onClick={() => removeRow(i)} title="Usuń wiersz">Usuń</button>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="modal-actions" style={{ justifyContent: 'space-between' }}>
                    <button className="btn-ghost" onClick={addRow}>+ Dodaj kolejny link</button>
                    <div>
                        <button className="btn-ghost" onClick={onClose}>Anuluj</button>
                        <button
                            className="btn-primary"
                            disabled={!allValid}
                            onClick={() => onSubmit(
                                rows.map(r => ({
                                    lang: r.lang as any,
                                    source_url: r.url.trim(),
                                    storage: 'LINK',
                                    narrator: r.narrator.trim(),
                                    license: r.license || null,
                                    is_visible: r.visible,
                                })) as any
                            )}
                        >
                            Dodaj linki
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function LinkAudioModal({
    open,
    defaultLang,
    onClose,
    onSubmit,
}: {
    open: boolean;
    defaultLang: Lang;
    onClose: () => void;
    onSubmit: (meta: AudioMeta) => void;
}) {
    const [lang, setLang] = useState<Lang>(defaultLang);
    const [url, setUrl] = useState('');
    const [narrator, setNarrator] = useState('');
    const [license, setLicense] = useState('');
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (!open) return;
        setLang(defaultLang);
        setUrl('');
        setNarrator('');
        setLicense('');
        setVisible(false);
    }, [open, defaultLang]);

    if (!open) return null;

    const valid = /^https?:\/\//i.test(url) && narrator.trim().length > 0;

    return (
        <div className="modal-backdrop">
            <div className="modal-card">
                <h3>Dodaj link do audio</h3>
                <div className="modal-form-grid">
                    <label>
                        <span>Język</span>
                        <select value={lang} onChange={e => setLang(ensureLang(e.target.value))}>
                            {ALLOWED.map(l => <option key={l} value={l}>{l}</option>)}
                        </select>
                    </label>
                    <label>
                        <span>URL</span>
                        <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://..." />
                    </label>
                    <label>
                        <span>Lektor *</span>
                        <input value={narrator} onChange={e => setNarrator(e.target.value)} placeholder="np. Jan Kowalski" />
                    </label>
                    <label>
                        <span>Licencja</span>
                        <input value={license} onChange={e => setLicense(e.target.value)} placeholder="np. CC-BY" />
                    </label>
                    <label className="inline">
                        <input type="checkbox" checked={visible} onChange={e => setVisible(e.target.checked)} />
                        Widoczne
                    </label>
                </div>
                <div className="modal-actions">
                    <button className="btn-ghost" onClick={onClose}>Anuluj</button>
                    <button
                        className="btn-primary"
                        disabled={!valid}
                        onClick={() => onSubmit({
                            lang: lang as any,
                            source_url: url.trim(),
                            storage: 'LINK',
                            narrator: narrator.trim(),
                            license: license || null,
                            is_visible: visible,
                        } as any)}
                    >
                        Dodaj link
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ================== Komponent: Inline edycja audio ================== */

function InlineAudioEditor({
    row,
    onCancel,
    onSave,
}: {
    row: AudioRow;
    onCancel: () => void;
    onSave: (patch: { language_code: Lang; narrator_name: string; license: string | null; is_visible: boolean }) => void;
}) {
    const [lang, setLang] = useState<Lang>(row.lang);
    const [narrator, setNarrator] = useState(row.narrator || '');
    const [license, setLicense] = useState(row.license || '');
    const [visible, setVisible] = useState<boolean>(row.is_visible ?? false);

    const canSave = narrator.trim().length > 0 || row.storage_type === 'LINK';

    return (
        <div className="inline-audio-editor">
            <div className="iae-grid">
                <label>
                    <span>Język</span>
                    <select value={lang} onChange={e => setLang(ensureLang(e.target.value))}>
                        {ALLOWED.map(l => <option key={l} value={l}>{l}</option>)}
                    </select>
                </label>
                <label>
                    <span>Lektor {row.storage_type === 'UPLOAD' ? '*' : ''}</span>
                    <input value={narrator} onChange={e => setNarrator(e.target.value)} placeholder="np. Jan Kowalski" />
                </label>
                <label>
                    <span>Licencja</span>
                    <input value={license} onChange={e => setLicense(e.target.value)} placeholder="np. CC-BY" />
                </label>
                <label className="inline">
                    <input type="checkbox" checked={visible} onChange={e => setVisible(e.target.checked)} />
                    Widoczne
                </label>
            </div>
            <div className="iae-actions">
                <button className="btn-primary" onClick={() => onSave({
                    language_code: lang,
                    narrator_name: narrator.trim(),
                    license: license || null,
                    is_visible: visible,
                })} disabled={!canSave}>Zapisz</button>
                <button className="btn-ghost" onClick={onCancel}>Anuluj</button>
            </div>
        </div>
    );
}

/* ================== Główny komponent ================== */

export default function AddContents({ onDone }: Props) {
    const { id } = useParams();
    const navigate = useNavigate();
    const contentIdFromRoute = id ? parseInt(id) : null;

    const [contentId, setContentId] = useState<number | null>(contentIdFromRoute);
    const [form, setForm] = useState<FormData>({
        title: '',
        description: '',
        text_content: '',
        read_time_minutes: '',
        age_category: AGE_CATEGORIES[0],
        is_visible: false,
        license: '',
        source_url: '',
        language: '',
        type: 'TEXT',
        is_premium: false,
        category_id: null,
        author_ids: [],
        tag_ids: [],
        difficulty_level: null,
    });

    const [initialPLText, setInitialPLText] = useState<string>('');
    const [initialPLVisible, setInitialPLVisible] = useState<boolean>(false);
    const [coverUrl, setCoverUrl] = useState<string | null>(null);
    const [coverFileName, setCoverFileName] = useState<string | null>(null);

    const [allAudios, setAllAudios] = useState<AudioRow[]>([]);
    const [editingAudioId, setEditingAudioId] = useState<number | null>(null);

    // autocomplety
    const [authorQuery, setAuthorQuery] = useState('');
    const [authorSuggestions, setAuthorSuggestions] = useState<any[]>([]);
    const [selectedAuthors, setSelectedAuthors] = useState<{ id: number; first_name: string; last_name: string }[]>([]);

    const [tagQuery, setTagQuery] = useState('');
    const [tagSuggestions, setTagSuggestions] = useState<any[]>([]);
    const [selectedTags, setSelectedTags] = useState<{ id: number; name: string }[]>([]);

    const [loading, setLoading] = useState(false);
    const [locked, setLocked] = useState(contentIdFromRoute != null);

    // tłumaczenia
    const [translations, setTranslations] = useState<TranslationRow[]>([]);
    const [trOpen, setTrOpen] = useState<Record<Lang, boolean>>({
        PL: false,
        EN: false,
        ES: false,
        DE: false,
        UK: false,
        IT: false,
        FR: false,
    });
    const [trAddOpen, setTrAddOpen] = useState(false);
    const [trLang, setTrLang] = useState<Lang>('EN');
    const [trText, setTrText] = useState<string>('');
    const [trDesc, setTrDesc] = useState<string>('');
    const [trVisible, setTrVisible] = useState<boolean>(false);
    const [trEditLang, setTrEditLang] = useState<Lang | null>(null);
    const [trEditText, setTrEditText] = useState<string>('');
    const [trEditDesc, setTrEditDesc] = useState<string>('');

    // modale audio
    const [audioModalOpen, setAudioModalOpen] = useState(false);
    const [audioModalFiles, setAudioModalFiles] = useState<File[]>([]);
    const [audioModalLang, setAudioModalLang] = useState<Lang>('PL');
    const [audioModalLockLang, setAudioModalLockLang] = useState(false);

    const [linkModalOpen, setLinkModalOpen] = useState(false);
    const [linkModalDefaultLang, setLinkModalDefaultLang] = useState<Lang>('PL');

    // dialogi informacyjne
    const [dlgOpen, setDlgOpen] = useState(false);
    const [dlgMsg, setDlgMsg] = useState('');
    const [dlgTitle, setDlgTitle] = useState<string | undefined>();
    const [dlgVariant, setDlgVariant] = useState<'ok' | 'error'>('ok');

    // audio linki (multi)
    const [linksModalOpen, setLinksModalOpen] = useState(false);
    const [linksModalDefaultLang, setLinksModalDefaultLang] = useState<Lang>('PL');
    const [linksModalLockLang, setLinksModalLockLang] = useState(false);
    const [linksModalContext, setLinksModalContext] = useState<null | { type: 'main' } | { type: 'tr', lang: Lang }>(null);

    const [categories, setCategories] = useState<Array<{ id: number; title: string }>>([]);

    const openLinksModalMain = () => {
        setLinksModalDefaultLang(ensureLang(form.language));
        setLinksModalLockLang(false);
        setLinksModalContext({ type: 'main' });
        setLinksModalOpen(true);
    };

    const openLinksModalForLang = (lang: Lang) => {
        setLinksModalDefaultLang(lang);
        setLinksModalLockLang(true);
        setLinksModalContext({ type: 'tr', lang });
        setLinksModalOpen(true);
    };

    const [confirmState, setConfirmState] = useState<{
        open: boolean;
        title?: string;
        message?: string;
        confirmLabel?: string;
        cancelLabel?: string;
        danger?: boolean;
        onConfirm?: () => void;
    }>({ open: false });

    const coverInputRef = useRef<HTMLInputElement | null>(null);
    const audioInputRef = useRef<HTMLInputElement | null>(null);

    const showOk = (msg: string, title = 'Sukces') => { setDlgVariant('ok'); setDlgTitle(title); setDlgMsg(msg); setDlgOpen(true); };
    const showErr = (msg: string, title = 'Błąd') => { setDlgVariant('error'); setDlgTitle(title); setDlgMsg(msg); setDlgOpen(true); };

    // ====== INIT — fetch do edycji ======
    useEffect(() => {
        if (!contentIdFromRoute) return;

        const fetchContent = async () => {
            try {
                const data = await getContentById(contentIdFromRoute);
                const { content, authors, tags } = data;

                setForm({
                    title: content.title || '',
                    description: content.description || '',
                    text_content: content.text_content || '',
                    read_time_minutes: content.read_time_minutes || '',
                    age_category: content.age_category || AGE_CATEGORIES[0],
                    is_visible: content.is_visible ?? false,
                    license: content.license || '',
                    source_url: content.source_url || '',
                    language: content.language || '',
                    type: content.type || 'TEXT',
                    is_premium: content.is_premium ?? false,
                    category_id: content.category_id ?? null,
                    author_ids: authors.map((a: any) => a.id),
                    tag_ids: tags.map((t: any) => t.id),
                    difficulty_level: content.difficulty_level || null,
                });

                setSelectedTags(tags);
                setSelectedAuthors(authors);
                setContentId(content.id);
                setCoverUrl(content.cover_image_url || null);
                setCoverFileName(content.cover_image_url?.split('/').pop() || null);

                const baseAudios = await listAudios(String(content.id));
                const mapped = baseAudios.map((a: any) => ({
                    id: a.id,
                    url: a.file_path,
                    lang: ensureLang(a.language_code),
                    narrator: a.narrator_name ?? null,
                    duration: a.duration_seconds ?? null,
                    license: a.license ?? null,
                    storage_type: a.storage_type,
                    source_url: a.source_url ?? null,
                    is_visible: a.is_visible ?? false,
                })) as AudioRow[];
                setAllAudios(mapped);

                const trs = await getTranslations(String(content.id));
                const trsMapped: TranslationRow[] = trs.map((t: any) => ({
                    id: t.id,
                    content_id: t.content_id,
                    language_code: ensureLang(t.language_code),
                    translated_text: t.translated_text ?? null,
                    translated_description: t.translated_description ?? null,
                    created_by: t.created_by ?? null,
                    last_edited_by: t.last_edited_by ?? null,
                    last_edited_at: t.last_edited_at ?? null,
                    is_visible: t.is_visible ?? false,
                    audios: (t.audios || []).map((a: any) => ({
                        id: a.id,
                        url: a.file_path,
                        lang: ensureLang(a.language_code),
                        narrator: a.narrator_name ?? null,
                        duration: a.duration_seconds ?? null,
                        license: a.license ?? null,
                        storage_type: a.storage_type,
                        source_url: a.source_url ?? null,
                        is_visible: a.is_visible ?? false,
                    }))
                }));
                setTranslations(trsMapped);
            } catch {
                showErr('Błąd przy wczytywaniu danych do edycji.');
            }
        };

        fetchContent();
    }, [contentIdFromRoute]);

    // ====== Autocomplete authors ======
    useEffect(() => {
        if (authorQuery.length < 2) {
            setAuthorSuggestions([]);
            return;
        }
        const delay = setTimeout(async () => {
            try {
                const results = await searchAuthors(authorQuery);
                setAuthorSuggestions(results);
            } catch {
                setAuthorSuggestions([]);
            }
        }, 300);
        return () => clearTimeout(delay);
    }, [authorQuery]);

    // ====== Autocomplete tags ======
    useEffect(() => {
        if (tagQuery.length < 2) {
            setTagSuggestions([]);
            return;
        }
        const delay = setTimeout(async () => {
            try {
                const results = await searchTags(tagQuery);
                setTagSuggestions(results);
            } catch {
                setTagSuggestions([]);
            }
        }, 300);
        return () => clearTimeout(delay);
    }, [tagQuery]);

    useEffect(() => {
        (async () => {
            try {
                const list = await listCategories();
                setCategories(list.map((c: any) => ({ id: c.id, title: c.title })));
                if (!contentId) {
                    const unc = list.find((c: any) => c.title === 'Uncategorized');
                    setForm((prev) => ({
                        ...prev,
                        category_id: unc ? unc.id : list[0]?.id ?? null,
                    }));
                }
            } catch { }
        })();
    }, [contentId]);

    // ====== Formularz: zmiany pól ======
    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const { name, value, type } = e.target;
        if (type === 'checkbox') {
            setForm((f) => ({ ...f, [name]: (e.target as HTMLInputElement).checked }));
        } else if (name === 'category_id') {
            setForm((f) => ({ ...f, category_id: value ? parseInt(value) : null }));
        } else {
            setForm((f) => ({ ...f, [name]: value }));
        }
    };

    // ====== Tworzenie treści ======
    const handleAddContent = async (e: React.FormEvent) => {
        e.preventDefault();
        if (contentId) return;
        setLoading(true);
        try {
            const res = await createContent(form);
            setContentId(res.id);
            if (form.tag_ids.length > 0) {
                await assignTagsToContent(res.id, form.tag_ids);
            }

            if (initialPLText.trim().length > 0) {
                await upsertTranslation(String(res.id), {
                    language_code: 'PL' as any,
                    translated_text: initialPLText.trim(),
                    is_visible: initialPLVisible,
                });
                await refreshTranslationsFor(res.id);
            }

            showOk('Treść utworzona! Dodaj okładkę, audio i tłumaczenia.');
        } catch (e: any) {
            showErr(e?.error || 'Błąd przy dodawaniu.');
        } finally {
            setLoading(false);
        }
    };

    // ====== Okładka ======
    const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || !contentId) return;
        const file = e.target.files[0];
        try {
            const res = await uploadCover(String(contentId), file);
            setCoverUrl(res.image);
            setCoverFileName(file.name);
            showOk('Okładka dodana!');
        } catch (e: any) {
            showErr(e?.error || 'Błąd przy uploadzie okładki.');
        }
    };

    // ====== GŁÓWNE AUDIO — upload ======
    const handleAudioUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || !contentId) return;
        const files = Array.from(e.target.files);
        setAudioModalLang(ensureLang(form.language));
        setAudioModalFiles(files);
        setAudioModalLockLang(false); // dla oryginału można zmieniać język
        setAudioModalOpen(true);
    };

    const submitAudioModal = async (rows: { file: File; meta: AudioMeta }[]) => {
        if (!contentId) return;
        try {
            await addAudios(String(contentId), rows.map(r => r.file), rows.map(r => r.meta));
            const auds = await listAudios(String(contentId));
            const mapped = auds.map((a: any) => ({
                id: a.id,
                url: a.file_path,
                lang: ensureLang(a.language_code),
                narrator: a.narrator_name ?? null,
                duration: a.duration_seconds ?? null,
                license: a.license ?? null,
                storage_type: a.storage_type,
                source_url: a.source_url ?? null,
                is_visible: a.is_visible ?? false,
            })) as AudioRow[];
            setAllAudios(mapped);
            showOk('Pliki audio dodane!');
            if (audioInputRef.current) audioInputRef.current.value = '';
        } catch (e: any) {
            showErr(e?.error || 'Błąd przy uploadzie audio.');
        } finally {
            setAudioModalOpen(false);
            setAudioModalFiles([]);
        }
    };

    const handleDeleteAudioById = async (a: AudioRow) => {
        if (!contentId) return;
        const name = a.url.split('/').pop() || `#${a.id}`;
        setConfirmState({
            open: true,
            title: 'Usunąć ten plik audio?',
            message: `Czy na pewno chcesz usunąć plik: ${name}?`,
            confirmLabel: 'Usuń',
            cancelLabel: 'Anuluj',
            danger: true,
            onConfirm: async () => {
                try {
                    await deleteAudio(String(contentId), a.id);
                    setAllAudios(prev => prev.filter(x => x.id !== a.id));
                    showOk('Plik audio usunięty.');
                } catch (e: any) {
                    showErr(e?.error || 'Błąd przy usuwaniu audio.');
                } finally {
                    setConfirmState(s => ({ ...s, open: false }));
                }
            }
        });
    };

    const saveInlineAudio = async (row: AudioRow, patch: { language_code: Lang; narrator_name: string; license: string | null; is_visible: boolean }) => {
        if (!contentId) return;
        try {
            await updateAudio(String(contentId), row.id, patch as any);
            const auds = await listAudios(String(contentId));
            const mapped = auds.map((a: any) => ({
                id: a.id,
                url: a.file_path,
                lang: ensureLang(a.language_code),
                narrator: a.narrator_name ?? null,
                duration: a.duration_seconds ?? null,
                license: a.license ?? null,
                storage_type: a.storage_type,
                source_url: a.source_url ?? null,
                is_visible: a.is_visible ?? false,
            })) as AudioRow[];
            setAllAudios(mapped);
            setEditingAudioId(null);
            showOk('Zaktualizowano meta audio.');
        } catch (e: any) {
            showErr(e?.error || 'Błąd przy edycji audio.');
        }
    };

    // ====== Update Content ======
    const handleUpdateContent = async () => {
        if (!contentId) return;
        setLoading(true);
        try {
            await updateContent(contentId, form);
            await assignTagsToContent(contentId, form.tag_ids);
            showOk('Zmiany zapisane.');
            if (onDone) onDone();
        } catch (e: any) {
            showErr(e?.error || 'Błąd przy zapisie zmian.');
        } finally {
            setLoading(false);
        }
    };

    // ====== TŁUMACZENIA ======
    const toggleTrOpen = (lang: Lang) => {
        setTrOpen(s => ({ ...s, [lang]: !s[lang] }));
    };

    const refreshTranslationsFor = async (idToRefresh: number) => {
        const trs = await getTranslations(String(idToRefresh));
        const trsMapped: TranslationRow[] = trs.map((t: any) => ({
            id: t.id,
            content_id: t.content_id,
            language_code: ensureLang(t.language_code),
            translated_text: t.translated_text ?? null,
            translated_description: t.translated_description ?? null,
            created_by: t.created_by ?? null,
            last_edited_by: t.last_edited_by ?? null,
            last_edited_at: t.last_edited_at ?? null,
            is_visible: t.is_visible ?? false,
            audios: (t.audios || []).map((a: any) => ({
                id: a.id,
                url: a.file_path,
                lang: ensureLang(a.language_code),
                narrator: a.narrator_name ?? null,
                duration: a.duration_seconds ?? null,
                license: a.license ?? null,
                storage_type: a.storage_type,
                source_url: a.source_url ?? null,
                is_visible: a.is_visible ?? false,
            }))
        }));
        setTranslations(trsMapped);
    };

    const refreshTranslations = async () => {
        if (!contentId) return;
        await refreshTranslationsFor(contentId);
    };

    const handleAddTranslation = async () => {
        if (!contentId) return;
        try {
            await upsertTranslation(String(contentId), {
                language_code: trLang as any,
                translated_text: trText || null,
                translated_description: trDesc || null,
                is_visible: trVisible,
            });
            await refreshTranslations();
            setTrAddOpen(false);
            setTrText('');
            setTrDesc('');
            setTrVisible(false);
            showOk('Dodano/zmieniono tłumaczenie.');
        } catch (e: any) {
            showErr(e?.error || 'Błąd przy dodawaniu tłumaczenia.');
        }
    };

    const startEditTr = (lang: Lang, currentText: string | null, currentDesc: string | null) => {
        setTrEditLang(lang);
        setTrEditText(currentText || '');
        setTrEditDesc(currentDesc || '');
    };

    const saveEditTr = async () => {
        if (!contentId || !trEditLang) return;
        try {
            await patchTranslation(String(contentId), trEditLang as any, {
                translated_text: trEditText,
                translated_description: trEditDesc,
            });
            await refreshTranslations();
            setTrEditLang(null);
            setTrEditText('');
            setTrEditDesc('');
            showOk('Zapisano tłumaczenie.');
        } catch (e: any) {
            showErr(e?.error || 'Błąd przy zapisie tłumaczenia.');
        }
    };

    const toggleTrVisibility = async (lang: Lang, current: boolean | undefined) => {
        if (!contentId) return;
        try {
            await patchTranslation(String(contentId), lang as any, { is_visible: !current });
            await refreshTranslations();
            showOk('Zmieniono widoczność tłumaczenia.');
        } catch (e: any) {
            showErr(e?.error || 'Błąd przy zmianie widoczności.');
        }
    };

    const removeTranslation = async (lang: Lang) => {
        if (!contentId) return;
        setConfirmState({
            open: true,
            title: `Usunąć tłumaczenie ${lang}?`,
            message: 'Operacji nie można cofnąć.',
            confirmLabel: 'Usuń',
            cancelLabel: 'Anuluj',
            danger: true,
            onConfirm: async () => {
                try {
                    await deleteTranslation(String(contentId), lang as any);
                    await refreshTranslations();
                    showOk('Tłumaczenie usunięte.');
                } catch (e: any) {
                    showErr(e?.error || 'Błąd przy usuwaniu tłumaczenia.');
                } finally {
                    setConfirmState(s => ({ ...s, open: false }));
                }
            }
        });
    };

    const openLinkModalForLang = (lang: Lang) => {
        openLinksModalForLang(lang);
    };

    const uploadAudioForLang = (lang: Lang, files: FileList | null) => {
        if (!files) return;
        const arr = Array.from(files);
        setAudioModalLang(lang);
        setAudioModalFiles(arr);
        setAudioModalLockLang(true); // BLOKUJEMY zmianę języka – audio przypisane do tego tłumaczenia
        setAudioModalOpen(true);
    };

    const submitLinksModal = async (metas: AudioMeta[]) => {
        if (!contentId) return;
        try {
            await addAudios(String(contentId), [], metas);
            if (linksModalContext?.type === 'main') {
                const auds = await listAudios(String(contentId));
                const mapped = auds.map((a: any) => ({
                    id: a.id,
                    url: a.file_path,
                    lang: ensureLang(a.language_code),
                    narrator: a.narrator_name ?? null,
                    duration: a.duration_seconds ?? null,
                    license: a.license ?? null,
                    storage_type: a.storage_type,
                    source_url: a.source_url ?? null,
                    is_visible: a.is_visible ?? false,
                })) as AudioRow[];
                setAllAudios(mapped);
            } else if (linksModalContext?.type === 'tr') {
                const lang = linksModalContext.lang;
                const auds = await listAudios(String(contentId), lang as any);
                setTranslations(prev => prev.map(t =>
                    t.language_code === lang
                        ? {
                            ...t,
                            audios: auds.map((a: any) => ({
                                id: a.id, url: a.file_path, lang: ensureLang(a.language_code),
                                narrator: a.narrator_name ?? null, duration: a.duration_seconds ?? null,
                                license: a.license ?? null, storage_type: a.storage_type, source_url: a.source_url ?? null,
                                is_visible: a.is_visible ?? false,
                            }))
                        }
                        : t
                ));
            }

            showOk('Dodano linki do audio.');
        } catch (e: any) {
            showErr(e?.error || 'Błąd przy dodawaniu linków audio.');
        } finally {
            setLinksModalOpen(false);
            setLinksModalContext(null);
        }
    };

    const submitLinkModal = async (meta: AudioMeta) => {
        if (!contentId) return;
        try {
            await addAudios(String(contentId), [], [meta]);
            const auds = await listAudios(String(contentId), meta.lang);
            setTranslations(prev => prev.map(t =>
                t.language_code === (meta.lang as Lang)
                    ? {
                        ...t,
                        audios: auds.map((a: any) => ({
                            id: a.id, url: a.file_path, lang: ensureLang(a.language_code),
                            narrator: a.narrator_name ?? null, duration: a.duration_seconds ?? null,
                            license: a.license ?? null, storage_type: a.storage_type, source_url: a.source_url ?? null,
                            is_visible: a.is_visible ?? false,
                        }))
                    }
                    : t
            ));
            showOk('Dodano link do audio.');
        } catch (e: any) {
            showErr(e?.error || 'Błąd przy dodawaniu linku audio.');
        } finally {
            setLinkModalOpen(false);
        }
    };

    const deleteLangAudio = async (lang: Lang, audio: AudioRow) => {
        if (!contentId) return;
        const name = audio.url.split('/').pop() || `#${audio.id}`;
        setConfirmState({
            open: true,
            title: 'Usunąć ten plik audio?',
            message: `Czy na pewno chcesz usunąć plik: ${name}?`,
            confirmLabel: 'Usuń',
            cancelLabel: 'Anuluj',
            danger: true,
            onConfirm: async () => {
                try {
                    await deleteAudio(String(contentId), audio.id);
                    setTranslations(prev => prev.map(t =>
                        t.language_code === lang
                            ? { ...t, audios: t.audios.filter(a => a.id !== audio.id) }
                            : t
                    ));
                    showOk('Audio usunięte.');
                } catch (e: any) {
                    showErr(e?.error || 'Błąd przy usuwaniu audio.');
                } finally {
                    setConfirmState(s => ({ ...s, open: false }));
                }
            }
        });
    };

    const saveTrInlineAudio = async (lang: Lang, row: AudioRow, patch: { language_code: Lang; narrator_name: string; license: string | null; is_visible: boolean }) => {
        if (!contentId) return;
        try {
            await updateAudio(String(contentId), row.id, patch as any);
            const auds = await listAudios(String(contentId), patch.language_code as any);
            setTranslations(prev => prev.map(t =>
                t.language_code === patch.language_code
                    ? {
                        ...t,
                        audios: auds.map((a: any) => ({
                            id: a.id, url: a.file_path, lang: ensureLang(a.language_code),
                            narrator: a.narrator_name ?? null, duration: a.duration_seconds ?? null,
                            license: a.license ?? null, storage_type: a.storage_type, source_url: a.source_url ?? null,
                            is_visible: a.is_visible ?? false,
                        }))
                    }
                    : t
            ));
            showOk('Zaktualizowano meta audio.');
        } catch (e: any) {
            showErr(e?.error || 'Błąd przy edycji audio.');
        }
    };

    function AudioUploadModal({
        open,
        defaultLang,
        files,
        lockLang = false,
        onClose,
        onSubmit,
    }: {
        open: boolean;
        defaultLang: Lang;
        files: File[];
        lockLang?: boolean;
        onClose: () => void;
        onSubmit: (rows: { file: File; meta: AudioMeta }[]) => void;
    }) {
        const [rows, setRows] = useState<
            { file: File; lang: Lang; narrator: string; license: string; is_visible: boolean; duration: number | null }[]
        >([]);

        useEffect(() => {
            if (!open) return;
            (async () => {
                const durations = await Promise.all(files.map(getAudioDuration));
                setRows(
                    files.map((f, i) => {
                        const base = f.name.split('.').slice(0, -1).join('.');
                        const [maybeLang, ...rest] = base.split('_');
                        const guessedLang = ensureLang(maybeLang);
                        const guessedNarrator = rest.join('_');
                        return {
                            file: f,
                            lang: lockLang ? defaultLang : (guessedLang || defaultLang),
                            narrator: guessedNarrator || '',
                            license: '',
                            is_visible: false,
                            duration: durations[i],
                        };
                    })
                );
            })();
        }, [open, files, defaultLang, lockLang]);

        if (!open) return null;
        const canSubmit = rows.every(r => r.narrator.trim().length > 0);

        return (
            <div className="modal-backdrop">
                <div className="modal-card">
                    <h3>Dodaj metadane audio</h3>
                    <p>Uzupełnij pola dla każdego pliku. <strong>Lektor jest wymagany.</strong></p>
                    <div className="modal-table">
                        {rows.map((r, idx) => (
                            <div className="modal-row" key={idx}>
                                <div className="cell name">{r.file.name}</div>
                                <div className="cell">
                                    <label>Język</label>
                                    <select
                                        value={r.lang}
                                        onChange={e => {
                                            if (lockLang) return;
                                            const v = ensureLang(e.target.value);
                                            setRows(prev => prev.map((x, i) => i === idx ? { ...x, lang: v } : x));
                                        }}
                                        disabled={lockLang}
                                    >
                                        {ALLOWED.map(l => <option key={l} value={l}>{l}</option>)}
                                    </select>
                                </div>
                                <div className="cell">
                                    <label>Lektor *</label>
                                    <input
                                        value={r.narrator}
                                        onChange={e => {
                                            const v = e.target.value;
                                            setRows(prev => prev.map((x, i) => i === idx ? { ...x, narrator: v } : x));
                                        }}
                                        placeholder="np. Jan Kowalski"
                                    />
                                </div>
                                <div className="cell">
                                    <label>Licencja</label>
                                    <input
                                        value={r.license}
                                        onChange={e => {
                                            const v = e.target.value;
                                            setRows(prev => prev.map((x, i) => i === idx ? { ...x, license: v } : x));
                                        }}
                                        placeholder="np. CC-BY"
                                    />
                                </div>
                                <div className="cell">
                                    <label>Widoczne</label>
                                    <input
                                        type="checkbox"
                                        checked={r.is_visible}
                                        onChange={e => {
                                            const v = e.target.checked;
                                            setRows(prev => prev.map((x, i) => i === idx ? { ...x, is_visible: v } : x));
                                        }}
                                    />
                                </div>
                                <div className="cell duration">{r.duration ? `${r.duration}s` : '—'}</div>
                            </div>
                        ))}
                    </div>

                    <div className="modal-actions">
                        <button className="btn-ghost" onClick={onClose}>Anuluj</button>
                        <button
                            className="btn-primary"
                            disabled={!canSubmit}
                            onClick={() => {
                                const payload = rows.map(r => ({
                                    file: r.file,
                                    meta: {
                                        fileName: r.file.name,
                                        category_id: form.category_id,
                                        lang: r.lang as any,
                                        narrator: r.narrator.trim(),
                                        duration: r.duration ?? null,
                                        license: r.license || null,
                                        is_visible: r.is_visible,
                                    } as AudioMeta
                                }));
                                onSubmit(payload);
                            }}
                        >
                            Wyślij
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ====== Widoki pomocnicze / logika tłumaczeń ======
    const inputProps = locked ? { disabled: true } : {};
    const originalLang = ensureLang(form.language);
    const originalAudios = allAudios.filter(a => a.lang === originalLang);

    const usedTranslationLangs = new Set(translations.map(t => t.language_code));
    const availableTrLangs = TRANSLATION_LANGS.filter(l => !usedTranslationLangs.has(l));

    useEffect(() => {
        if (!trAddOpen) return;
        if (availableTrLangs.length === 0) return;
        if (!availableTrLangs.includes(trLang)) {
            setTrLang(availableTrLangs[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trAddOpen, translations]);

    return (
        <>
            <div className="contents-add-container">
                <div className="breadcrumb">
                    <span onClick={() => navigate('/admin')} className="breadcrumb-link">Dashboard</span> /
                    <span onClick={() => navigate('/admin/contents')} className="breadcrumb-link">Treść</span> /
                    <span>{contentIdFromRoute ? ` Edytuj treść / ${id}` : 'Dodaj treść'}</span>
                </div>

                <div className="title-bar">
                    <h2>{contentIdFromRoute ? 'Edytuj treść' : 'Dodaj treść'}</h2>

                    <div className='edit-buttons'>
                        <div className="form-buttons">
                            {contentIdFromRoute == null ? (
                                <button form="add-content-form" type="submit" disabled={loading}>
                                    {loading ? 'Dodaję...' : 'Dodaj treść'}
                                </button>
                            ) : (
                                <button onClick={handleUpdateContent} type="button" disabled={loading || locked}>
                                    {loading ? 'Zapisuję...' : 'Zapisz zmiany'}
                                </button>
                            )}
                        </div>
                        {contentIdFromRoute !== null ?
                            <button
                                onClick={() => setLocked((prev) => !prev)}
                                className={`lock-toggle ${locked ? 'is-locked' : 'is-unlocked'}`}
                                title={locked ? 'Odblokuj edycję' : 'Zablokuj edycję'}
                                aria-label={locked ? 'Odblokuj edycję' : 'Zablokuj edycję'}
                                type="button"
                            >
                                <LockIcon locked={locked} />
                            </button>
                            : null
                        }
                    </div>

                </div>

                <div className="contents-add-wrapper">
                    {/* LEWA KOLUMNA — oryginalny content */}
                    <div className="form-column">
                        <form id="add-content-form" onSubmit={handleAddContent} className="add-content-form">
                            <label>
                                Tytuł
                                <input
                                    name="title"
                                    value={form.title}
                                    onChange={handleChange}
                                    placeholder="Wpisz tytuł treści"
                                    required
                                    {...inputProps}
                                />
                            </label>

                            <label>
                                Kategoria
                                <select
                                    value={form.category_id ?? ''}
                                    onChange={(e) =>
                                        setForm({ ...form, category_id: e.target.value ? Number(e.target.value) : null })
                                    }
                                >
                                    {categories.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.title}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label>
                                Typ treści
                                <select name="type" value={form.type} onChange={handleChange} {...inputProps}>
                                    <option value="TEXT">Treść</option>
                                    <option value="AUDIO">Audio</option>
                                    <option value="BOTH">Treść + Audio</option>
                                </select>
                            </label>

                            {/* <label>
                                Opis
                                <textarea
                                    name="description"
                                    value={form.description}
                                    onChange={handleChange}
                                    placeholder="Wpisz krótki opis (lead)"
                                    {...inputProps}
                                />
                            </label> */}

                            <label>
                                Treść
                                <textarea
                                    name="text_content"
                                    value={form.text_content}
                                    onChange={handleChange}
                                    placeholder="Treść artykułu / opowiadania"
                                    required
                                    {...inputProps}
                                />
                            </label>

                            {/* <label>
                                Czas czytania (minuty)
                                <input
                                    name="read_time_minutes"
                                    type="number"
                                    value={form.read_time_minutes}
                                    onChange={handleChange}
                                    placeholder="Np. 5"
                                    min={1}
                                    {...inputProps}
                                />
                            </label> */}

                            {/* <label>
                                Kategoria wiekowa
                                <select name="age_category" value={form.age_category} onChange={handleChange} {...inputProps}>
                                    {AGE_CATEGORIES.map((cat) => (
                                        <option value={cat} key={cat}>{cat}</option>
                                    ))}
                                </select>
                            </label> */}

                            {/* <label>
                                Licencja
                                <input
                                    name="license"
                                    value={form.license}
                                    onChange={handleChange}
                                    placeholder="Np. CC-BY"
                                    {...inputProps}
                                />
                            </label> */}

                            {/* <label>
                                Źródło (opcjonalnie)
                                <input
                                    name="source_url"
                                    value={form.source_url}
                                    onChange={handleChange}
                                    placeholder="https://adres-źródła..."
                                    {...inputProps}
                                />
                            </label> */}

                            {/* <label>
                                Język oryginału
                                <input
                                    name="language"
                                    value={form.language}
                                    onChange={handleChange}
                                    placeholder="Np. PL, EN…"
                                    {...inputProps}
                                />
                            </label> */}

                            <label>
                                Poziom trudności (PL)
                                <select
                                    value={form.difficulty_level || ''}
                                    onChange={(e) =>
                                        setForm((f) => ({
                                            ...f,
                                            difficulty_level: e.target.value
                                                ? (e.target.value as Difficulty)
                                                : null,
                                        }))
                                    }
                                    disabled={locked}
                                >
                                    <option value="">— brak —</option>
                                    <option value="EASY">łatwy</option>
                                    <option value="MEDIUM">średni</option>
                                    <option value="HARD">trudny</option>
                                </select>
                            </label>

                            <div className="toggle-row">
                                <label className={`violet-toggle ${locked ? 'is-disabled' : ''}`}>
                                    <input
                                        type="checkbox"
                                        name="is_premium"
                                        checked={form.is_premium}
                                        onChange={handleChange}
                                        disabled={locked}
                                    />
                                    <span className="vt-track" aria-hidden="true"><span className="vt-thumb" /></span>
                                    <span className="vt-text">
                                        Premium <span className="vt-state">{form.is_premium ? 'TAK' : 'NIE'}</span>
                                    </span>
                                </label>

                                <label className={`violet-toggle ${locked ? 'is-disabled' : ''}`}>
                                    <input
                                        type="checkbox"
                                        name="is_visible"
                                        checked={form.is_visible}
                                        onChange={handleChange}
                                        disabled={locked}
                                    />
                                    <span className="vt-track" aria-hidden="true"><span className="vt-thumb" /></span>
                                    <span className="vt-text">
                                        Widoczny <span className="vt-state">{form.is_visible ? 'TAK' : 'NIE'}</span>
                                    </span>
                                </label>
                            </div>

                            {selectedAuthors.length > 0 && (
                                <div className="chips-section">
                                    <div className="chips-title">Autorzy</div>
                                    <ul className="chip-list">
                                        {selectedAuthors.map((author) => (
                                            <li key={author.id} className="chip">
                                                {author.first_name} {author.last_name}
                                                {!locked && (
                                                    <button
                                                        type="button"
                                                        className="chip-remove"
                                                        onClick={() => {
                                                            setSelectedAuthors((prev) => prev.filter((a) => a.id !== author.id));
                                                            setForm((f) => ({ ...f, author_ids: f.author_ids.filter((id2) => id2 !== author.id) }));
                                                        }}
                                                        aria-label={`Usuń autora ${author.first_name} ${author.last_name}`}
                                                        title="Usuń"
                                                    >
                                                        ×
                                                    </button>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* <div className="autocomplete-wrapper">
                                <input
                                    type="text"
                                    value={authorQuery}
                                    onChange={(e) => setAuthorQuery(e.target.value)}
                                    placeholder="Dodaj autora (imię lub nazwisko)"
                                    disabled={locked}
                                />
                                {authorSuggestions.length > 0 && (
                                    <ul className="autocomplete-list">
                                        {authorSuggestions.map((author) => (
                                            <li
                                                key={author.id}
                                                className="autocomplete-item"
                                                onClick={() => {
                                                    if (!form.author_ids.includes(author.id)) {
                                                        setSelectedAuthors((prev) => [...prev, author]);
                                                        setForm((f) => ({ ...f, author_ids: [...f.author_ids, author.id] }));
                                                    }
                                                    setAuthorQuery('');
                                                    setAuthorSuggestions([]);
                                                }}
                                            >
                                                {author.first_name} {author.last_name}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div> */}

                            {selectedTags.length > 0 && (
                                <div className="chips-section">
                                    <div className="chips-title">Tagi</div>
                                    <ul className="chip-list">
                                        {selectedTags.map((tag) => (
                                            <li key={tag.id} className="chip">
                                                {tag.name}
                                                {!locked && (
                                                    <button
                                                        type="button"
                                                        className="chip-remove"
                                                        onClick={() => {
                                                            setSelectedTags((prev) => prev.filter((t) => t.id !== tag.id));
                                                            setForm((f) => ({ ...f, tag_ids: f.tag_ids.filter((id2) => id2 !== tag.id) }));
                                                        }}
                                                        aria-label={`Usuń tag ${tag.name}`}
                                                        title="Usuń"
                                                    >
                                                        ×
                                                    </button>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div className="autocomplete-wrapper">
                                <input
                                    type="text"
                                    value={tagQuery}
                                    onChange={(e) => setTagQuery(e.target.value)}
                                    placeholder="Dodaj tag"
                                    disabled={locked}
                                />
                                {tagSuggestions.length > 0 && (
                                    <ul className="autocomplete-list">
                                        {tagSuggestions.map((tag) => (
                                            <li
                                                key={tag.id}
                                                className="autocomplete-item"
                                                onClick={() => {
                                                    if (!form.tag_ids.includes(tag.id)) {
                                                        setSelectedTags((prev) => [...prev, tag]);
                                                        setForm((f) => ({ ...f, tag_ids: [...f.tag_ids, tag.id] }));
                                                    }
                                                    setTagQuery('');
                                                    setTagSuggestions([]);
                                                }}
                                            >
                                                {tag.name}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </form>
                    </div>

                    {/* PRAWA KOLUMNA — media i tłumaczenia */}
                    <div className="media-column">
                        {!contentId ? (
                            <div className="add-info">Zapisz treść, aby dodać okładkę, audio i tłumaczenia.</div>
                        ) : (
                            <>
                                <div className="upload-section">
                                    <h3>Dodaj okładkę</h3>

                                    {coverUrl && (
                                        <div className="cover-preview">
                                            <img src={coverUrl} alt="Okładka" className="cover-img" />
                                        </div>
                                    )}
                                    <FilePicker
                                        accept="image/*"
                                        onChange={handleCoverUpload}
                                        disabled={locked}
                                        inputRef={coverInputRef as React.RefObject<HTMLInputElement>}
                                        buttonLabel="Wybierz obraz"
                                        className="file-picker-cover"
                                    />
                                    <button
                                        className="delete-btn"
                                        onClick={async () => {
                                            if (!contentId || !coverFileName) return;
                                            try {
                                                await deleteCover(contentId, coverFileName);
                                                setCoverUrl(null);
                                                setCoverFileName(null);
                                                showOk('Okładka usunięta.');
                                            } catch {
                                                showErr('Błąd przy usuwaniu okładki.');
                                            }
                                        }}
                                        disabled={locked}
                                    >
                                        Usuń okładkę
                                    </button>
                                </div>

                                <div className="upload-section">
                                    <h3>Dodaj pliki audio (oryginał: {originalLang})</h3>
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                        <FilePicker
                                            accept="audio/*"
                                            multiple
                                            onChange={handleAudioUpload}
                                            disabled={locked}
                                            inputRef={audioInputRef}
                                            buttonLabel="Wybierz pliki audio"
                                            className="file-picker--audio"
                                        />
                                        <button
                                            type="button"
                                            className="btn-link-add"
                                            onClick={openLinksModalMain}
                                            disabled={locked}
                                        >
                                            + Dodaj linki do audio
                                        </button>
                                    </div>
                                    <ul className="audio-list">
                                        {originalAudios.length === 0 && (
                                            <li className="audio-list-item">
                                                <span className="audio-meta">Brak plików w języku oryginału.</span>
                                            </li>
                                        )}
                                        {originalAudios.map((file) => (
                                            <li key={file.id} className="audio-list-item" style={{ width: '100%', flexDirection: 'column', alignItems: 'stretch' }}>
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
                                                    <a href={file.url} target="_blank" rel="noreferrer">{file.url.split('/').pop()}</a>
                                                    <span className="audio-meta">
                                                        {file.lang}
                                                        {file.narrator ? ` • ${file.narrator}` : ''}
                                                        {file.duration ? ` • ${Math.round(file.duration)}s` : ''}
                                                        {file.license ? ` • ${file.license}` : ''}
                                                        {file.storage_type ? ` • ${file.storage_type}` : ''}
                                                        {` • ${file.is_visible ? 'widoczne' : 'ukryte'}`}
                                                    </span>
                                                    <div style={{ marginRight: 'auto', display: 'flex', gap: 8 }}>
                                                        <button className="btn-ghost" onClick={() => setEditingAudioId(prev => prev === file.id ? null : file.id)} disabled={locked}>
                                                            <EditIcon />
                                                        </button>
                                                        <button className="delete-btn" onClick={() => handleDeleteAudioById(file)} disabled={locked}>
                                                            <TrashIcon />
                                                        </button>
                                                    </div>
                                                </div>

                                                {editingAudioId === file.id && (
                                                    <InlineAudioEditor
                                                        row={file}
                                                        onCancel={() => setEditingAudioId(null)}
                                                        onSave={(patch) => saveInlineAudio(file, patch)}
                                                    />
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* MODAL: meta dla uploadowanych audio */}
                <AudioUploadModal
                    open={audioModalOpen}
                    defaultLang={audioModalLang}
                    files={audioModalFiles}
                    lockLang={audioModalLockLang}
                    onClose={() => { setAudioModalOpen(false); setAudioModalFiles([]); }}
                    onSubmit={submitAudioModal}
                />
            </div>

            {/* BLOK: TŁUMACZENIA */}
            <div className="translations-card">
                <div className="tr-header">
                    <h3 style={{ margin: 0 }}>Tłumaczenia</h3>
                    <button
                        className="btn-ghost"
                        type="button"
                        onClick={() => setTrAddOpen(v => !v)}
                        disabled={locked}
                    >
                        {trAddOpen ? 'Zamknij' : 'Dodaj tłumaczenie'}
                    </button>
                </div>

                {trAddOpen && (
                    <div className="translation-form">
                        <label>
                            Język tłumaczenia
                            {availableTrLangs.length === 0 ? (
                                <div className="muted" style={{ marginTop: 4 }}>
                                    Wszystkie dostępne języki tłumaczeń są już wykorzystane.
                                </div>
                            ) : (
                                <select
                                    value={trLang}
                                    onChange={e => setTrLang(ensureLang(e.target.value))}
                                    disabled={locked || availableTrLangs.length === 0}
                                >
                                    {availableTrLangs.map(l => (
                                        <option key={l} value={l}>{l}</option>
                                    ))}
                                </select>
                            )}
                        </label>

                        <label>
                            Tekst tłumaczenia
                            <textarea
                                placeholder="Tutaj wpisz tekst tłumaczenia..."
                                value={trText}
                                onChange={e => setTrText(e.target.value)}
                                disabled={locked}
                            />
                        </label>

                        <label>
                            Opis tłumaczenia (lead)
                            <textarea
                                placeholder="Krótki opis/lead tłumaczenia..."
                                value={trDesc}
                                onChange={e => setTrDesc(e.target.value)}
                                disabled={locked}
                            />
                        </label>

                        <label className={`violet-toggle ${locked ? 'is-disabled' : ''}`}>
                            <input
                                type="checkbox"
                                checked={trVisible}
                                onChange={e => setTrVisible(e.target.checked)}
                                disabled={locked}
                            />
                            <span className="vt-track" aria-hidden="true"><span className="vt-thumb" /></span>
                            <span className="vt-text">
                                Widoczny <span className="vt-state">{trVisible ? 'TAK' : 'NIE'}</span>
                            </span>
                        </label>

                        <div className="form-buttons">
                            <button
                                type="button"
                                onClick={handleAddTranslation}
                                disabled={locked || availableTrLangs.length === 0}
                            >
                                Zapisz tłumaczenie
                            </button>
                        </div>
                    </div>
                )}

                {translations.length === 0 ? (
                    <div className="add-info" style={{ marginTop: 12 }}>Brak tłumaczeń.</div>
                ) : (
                    <ul className="tr-accordion" style={{ marginTop: 12, listStyle: 'none', padding: 0 }}>
                        {translations.map(tr => {
                            const open = !!trOpen[tr.language_code];
                            const isEditing = trEditLang === tr.language_code;

                            return (
                                <TranslationItem
                                    key={tr.language_code}
                                    tr={tr}
                                    open={open}
                                    isEditing={isEditing}
                                    locked={locked}
                                    onToggle={() => toggleTrOpen(tr.language_code)}
                                    onStartEditText={() => startEditTr(tr.language_code, tr.translated_text, tr.translated_description)}
                                    onSaveText={saveEditTr}
                                    onCancelEditText={() => { setTrEditLang(null); setTrEditText(''); }}
                                    trEditText={trEditText}
                                    setTrEditText={setTrEditText}
                                    trEditDesc={trEditDesc}
                                    setTrEditDesc={setTrEditDesc}
                                    onToggleVisibility={() => toggleTrVisibility(tr.language_code, tr.is_visible)}
                                    onRemove={() => removeTranslation(tr.language_code)}
                                    onRefreshAudios={async () => {
                                        if (!contentId) return;
                                        const auds = await listAudios(String(contentId), tr.language_code as any);
                                        setTranslations(prev => prev.map(p =>
                                            p.language_code === tr.language_code
                                                ? {
                                                    ...p,
                                                    audios: auds.map((a: any) => ({
                                                        id: a.id, url: a.file_path, lang: ensureLang(a.language_code),
                                                        narrator: a.narrator_name ?? null, duration: a.duration_seconds ?? null,
                                                        license: a.license ?? null, storage_type: a.storage_type, source_url: a.source_url ?? null,
                                                        is_visible: a.is_visible ?? false,
                                                    }))
                                                }
                                                : p
                                        ));
                                    }}
                                    onAddLink={() => openLinkModalForLang(tr.language_code)}
                                    onUploadForLang={(lang, files) => uploadAudioForLang(lang, files)}
                                    onDeleteAudio={(audio) => deleteLangAudio(tr.language_code, audio)}
                                    onSaveAudio={(row, patch) => saveTrInlineAudio(tr.language_code, row, patch)}
                                />
                            );
                        })}
                    </ul>
                )}
            </div>

            {/* MODAL: Dodaj linki do audio (multi) */}
            <LinkAudiosModal
                open={linksModalOpen}
                defaultLang={linksModalDefaultLang}
                lockLang={linksModalLockLang}
                onClose={() => { setLinksModalOpen(false); setLinksModalContext(null); }}
                onSubmit={submitLinksModal}
            />

            {/* (Opcjonalny) MODAL: pojedynczy link */}
            <LinkAudioModal
                open={linkModalOpen}
                defaultLang={linkModalDefaultLang}
                onClose={() => setLinkModalOpen(false)}
                onSubmit={submitLinkModal}
            />

            {/* Dialogi */}
            <Dialog open={dlgOpen} title={dlgTitle} message={dlgMsg} variant={dlgVariant} onClose={() => setDlgOpen(false)} />
            <ConfirmDialog
                open={confirmState.open}
                title={confirmState.title}
                message={confirmState.message}
                confirmLabel={confirmState.confirmLabel}
                cancelLabel={confirmState.cancelLabel}
                danger={confirmState.danger}
                onConfirm={confirmState.onConfirm || (() => setConfirmState(s => ({ ...s, open: false })))}
                onCancel={() => setConfirmState(s => ({ ...s, open: false }))}
            />
            <AddQuiz
                contentId={contentId}
                baseLanguage={ensureLang(form.language) as any}
                locked={locked}
                hasText={!!form.text_content}
            />
        </>
    );
}

/* Komponent pojedynczego tłumaczenia */
function TranslationItem({
    tr,
    open,
    isEditing,
    locked,
    onToggle,
    onStartEditText,
    onSaveText,
    onCancelEditText,
    trEditText,
    setTrEditText,
    trEditDesc,
    setTrEditDesc,
    onToggleVisibility,
    onRemove,
    onRefreshAudios,
    onAddLink,
    onUploadForLang,
    onDeleteAudio,
    onSaveAudio,
}: {
    tr: TranslationRow;
    open: boolean;
    isEditing: boolean;
    locked: boolean;
    onToggle: () => void;
    onStartEditText: () => void;
    onSaveText: () => void;
    onCancelEditText: () => void;
    trEditText: string;
    setTrEditText: (v: string) => void;
    trEditDesc: string;
    setTrEditDesc: (v: string) => void;
    onToggleVisibility: () => void;
    onRemove: () => void;
    onRefreshAudios: () => void;
    onAddLink: () => void;
    onUploadForLang: (lang: Lang, files: FileList | null) => void;
    onDeleteAudio: (audio: AudioRow) => void;
    onSaveAudio: (row: AudioRow, patch: { language_code: Lang; narrator_name: string; license: string | null; is_visible: boolean }) => void;
}) {
    const [editingAudioId, setEditingAudioId] = useState<number | null>(null);
    const fileRef = useRef<HTMLInputElement>(null);

    return (
        <li className={`tr-item ${open ? 'open' : ''}`}>
            <button
                type="button"
                className="tr-toggle"
                onClick={onToggle}
            >
                <span className="tr-lang">
                    {tr.language_code} {tr.is_visible ? '' : '(ukryte)'}
                </span>
                <span className="tr-meta">
                    Ostatnia edycja: {tr.last_edited_at ? new Date(tr.last_edited_at).toLocaleString() : '—'}
                </span>
            </button>

            {open && (
                <div className="tr-body">
                    {!isEditing ? (
                        <div className="tr-text">
                            <div className="actions-row mt-6" style={{ gap: 8 }}>
                                <button className="btn-ghost" onClick={onStartEditText} disabled={locked}>
                                    Edytuj tłumaczenie
                                </button>
                                <button className="btn-ghost" onClick={onToggleVisibility} disabled={locked}>
                                    {tr.is_visible ? 'Ukryj' : 'Pokaż'}
                                </button>
                                <button className="delete-btn" onClick={onRemove} disabled={locked}>
                                    Usuń tłumaczenie
                                </button>
                            </div>
                            {tr.translated_text ? (
                                <div style={{ whiteSpace: 'pre-wrap' }}>{tr.translated_text}</div>
                            ) : (
                                <span className="muted">Brak tekstu.</span>
                            )}
                            <div className="mt-4">
                                <strong>Opis:</strong>{' '}
                                {tr.translated_description
                                    ? <span style={{ whiteSpace: 'pre-wrap' }}>{tr.translated_description}</span>
                                    : <span className="muted">Brak opisu.</span>}
                            </div>
                        </div>
                    ) : (
                        <div className="tr-text-edit">
                            <textarea
                                value={trEditText}
                                onChange={e => setTrEditText(e.target.value)}
                                disabled={locked}
                            />
                            <label className="mt-3" style={{ display: 'block' }}>
                                <span>Opis tłumaczenia (lead)</span>
                                <textarea
                                    value={trEditDesc}
                                    onChange={e => setTrEditDesc(e.target.value)}
                                    disabled={locked}
                                />
                            </label>
                            <div className="actions-row mt-6">
                                <button className="btn-primary" onClick={onSaveText} disabled={locked}>Zapisz</button>
                                <button className="btn-ghost" onClick={onCancelEditText}>Anuluj</button>
                            </div>
                        </div>
                    )}

                    <div className="tr-audios">
                        <div className="tr-audios-head">
                            <h4>Audio ({tr.language_code})</h4>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <button className="btn-ghost" onClick={onRefreshAudios}>Odśwież audio</button>
                                <button className="btn-link-add" onClick={onAddLink}>+ Dodaj link do audio</button>
                            </div>
                        </div>

                        {tr.audios.length === 0 ? (
                            <div className="state muted">Brak plików.</div>
                        ) : (
                            <ul className="audio-list">
                                {tr.audios.map(a => (
                                    <li key={a.id} className="audio-list-item" style={{ width: '100%', flexDirection: 'column', alignItems: 'stretch' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <a href={a.url} target="_blank" rel="noreferrer">{a.url.split('/').pop()}</a>
                                            <span className="audio-meta">
                                                {a.lang}{a.narrator ? ` • ${a.narrator}` : ''}{a.duration ? ` • ${Math.round(a.duration)}s` : ''}{a.license ? ` • ${a.license}` : ''}{a.storage_type ? ` • ${a.storage_type}` : ''}{` • ${a.is_visible ? 'widoczne' : 'ukryte'}`}
                                            </span>
                                            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                                                <button className="btn-ghost" onClick={() => setEditingAudioId(prev => prev === a.id ? null : a.id)} disabled={locked}>
                                                    <EditIcon />
                                                </button>
                                                <button className="delete-btn" onClick={() => onDeleteAudio(a)} disabled={locked}> <TrashIcon /></button>
                                            </div>
                                        </div>
                                        {editingAudioId === a.id && (
                                            <InlineAudioEditor
                                                row={a}
                                                onCancel={() => setEditingAudioId(null)}
                                                onSave={(patch) => onSaveAudio(a, patch)}
                                            />
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}

                        <div className="upload-section mt-10">
                            <h5>Dodaj audio ({tr.language_code})</h5>
                            <div className="file-picker">
                                <input
                                    ref={fileRef}
                                    type="file"
                                    accept="audio/*"
                                    multiple
                                    onChange={(e) => onUploadForLang(tr.language_code, e.target.files)}
                                    disabled={locked}
                                    className="file-input-hidden"
                                />
                                <button
                                    type="button"
                                    className="btn-file"
                                    disabled={locked}
                                    onClick={() => fileRef.current?.click()}
                                >
                                    Wybierz pliki audio
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </li>
    );
}

/* większa ikona kłódki */
function LockIcon({ locked }: { locked: boolean }) {
    return locked ? (
        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="4" y="10" width="16" height="10" rx="2"></rect>
            <path d="M8 10V7a4 4 0 0 1 8 0v3"></path>
        </svg>
    ) : (
        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="4" y="11" width="16" height="9" rx="2"></rect>
            <path d="M8 11V7a 4 4 0 0 1 7.5-2"></path>
        </svg>
    );
}

function EditIcon() {
    return (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M12 20h9"></path>
            <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"></path>
        </svg>
    );
}

function TrashIcon() {
    return (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M3 6h18"></path>
            <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
        </svg>
    );
}
